import { FC, useMemo, useState } from 'react';

import {
  DIALOG_BLUE_BODY,
  DIALOG_BLUE_BUTTON,
  ModalButton,
  READ_ALOUD_HIGHLIGHT_BLUE,
  Readable,
  modalStyle,
  NARROW_PROBLEM_WIDTH,
  PROBLEM_WIDTH,
  wrapPanelSupplier,
  buildProblemCanvasOwner,
  ProblemCanvas,
} from '@gmm/problem';

import { getConfig } from '../../../environmentConfig';
import { changeHandStatus } from '../../../studentApp/api/sendRaisedHand/util';
import { useLayout } from '../../../studentApp/hooks';
import { bannerStore, problemState } from '../../../studentApp/stores';
import { studentAppModalStore } from '../../../studentApp/stores/studentAppModalStore';
import { animateReplaceProblem } from '../../../studentApp/utils/gmmUtils';
import { MOBILE_MARGIN_PX } from '../../constants';
import { DraggableTop } from '../util/constants';

import { DraggableResizable } from './DraggableResizable';
import {
  getHelpSizing,
  HELP_PANEL_PADDING,
  SHOW_ANSWER_HORIZONTAL_PADDING,
} from './showAnswerSizing';

export const ShowAnswer: FC<{ close: () => void }> = ({ close }) => {
  const [problemData, help] = studentAppModalStore(s => [
    s.answerToCurrentProblem,
    s.help,
  ]);
  const handRaisedProblemId = bannerStore(s => s.handRaisedProblemId);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const minimizedMessage = isCollapsed ? 'Answer' : '';
  const permitReadAloud = bannerStore(s => s.permitReadAloud);
  const { isLandscape } = useLayout();

  // Determine the left edge position of the problem canvas for landscape layout
  // so we can align the right edge of the modal with it. In other layouts, we
  // just center the modal, so no need to calculate the canvas position.
  const problemCanvasLeft = useMemo(() => {
    if (!isLandscape) return undefined;
    const problemHolder = document.getElementById('scrollableProblemHolder');
    const problemHolderLeft = problemHolder?.getBoundingClientRect().left;
    const problemHolderWidth = problemHolder?.getBoundingClientRect().width;
    // The problem canvas is centered within the problem holder, so we need to
    // shift the left position by half of the remaining space in the problem
    // holder after accounting for the problem width.
    const problemCanvasOffset = problemHolderWidth
      ? (problemHolderWidth - PROBLEM_WIDTH) / 2
      : 0;

    return problemHolderLeft !== undefined
      ? problemHolderLeft + problemCanvasOffset
      : undefined;
  }, [isLandscape]);

  const replace = (): void => {
    if (handRaisedProblemId === problemState().selectedID)
      changeHandStatus(false);
    close();
    animateReplaceProblem();
  };

  // Declare the rendering owner first so it can be reused for measurement below.
  // This ensures the measured width accounts for the same read-aloud button
  // injection and other owner settings that will be used during actual rendering,
  // preventing a mismatch that would cause the canvas to be cut off.
  const problemCanvasOwner = useMemo(() => {
    return buildProblemCanvasOwner({
      config: getConfig(),
      isPermitReadAloud: () => permitReadAloud,
    });
    // Deliberate omission of permitReadAloud in dependency array:
    // Teacher change to read aloud setting will not affect already opened modals
  }, []);

  const measuredHelpWidth = useMemo(() => {
    if (!help) {
      return NARROW_PROBLEM_WIDTH;
    }

    // Use the rendering owner so that read-aloud button injection and other
    // owner state are identical between this measurement and the actual render.
    // Using PROBLEM_WIDTH for the measurement canvas ensures wide-mode
    // (screenMaxTextW = MAX_MULTI_LINE_WIDTH), so narrow-mode wrapping does not
    // distort the help content's natural width.
    problemCanvasOwner.setCanvas(
      document.createElement('canvas'),
      PROBLEM_WIDTH
    );
    const panel = problemCanvasOwner.getStaticPanel(help);

    return panel.viewportW;
    // problemCanvasOwner is deliberately omitted: it is memoized with [] and is
    // therefore stable for the lifetime of this modal. Only re-measure when help changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [help]);

  // The panel minimum must also apply to its usable canvas area. Otherwise a
  // very narrow natural measurement becomes the ProblemCanvas layout width and
  // can wrap help that has ample space in the surrounding panel.
  const { helpCanvasWidth, helpPanelWidth } = getHelpSizing(measuredHelpWidth);

  // help arrives in a PanelSupplier dto, not a ProblemData dto, so need to wrap it
  const assimilatedProblemData = help ? wrapPanelSupplier(help) : problemData;

  return (
    <DraggableResizable
      collapsed={isCollapsed}
      alignRightEdgeToX={isLandscape ? problemCanvasLeft : undefined}
      shouldBeBottomAligned={!isLandscape}
      style={{
        color: modalStyle.color,
        borderRadius: modalStyle.borderRadius,
        boxShadow: modalStyle.boxShadow,
        backgroundColor: DIALOG_BLUE_BODY,
        overflow: 'hidden',
        zIndex: modalStyle.zIndex,
        maxWidth: window.innerWidth - 2 * MOBILE_MARGIN_PX,
      }}
    >
      <DraggableTop
        dragHandle="draggable-handle"
        backgroundColor={DIALOG_BLUE_BODY}
        close={replace}
        minimizedMessage={minimizedMessage}
        isCollapsed={isCollapsed}
        toggleCollapse={() => setIsCollapsed(c => !c)}
      />

      {!isCollapsed && (
        <div
          style={{
            padding: `3px ${SHOW_ANSWER_HORIZONTAL_PADDING}px 20px`,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {/* Title */}
          {!help && (
            <div
              style={{
                textAlign: 'center',
                fontSize: '130%',
                fontWeight: 'bold',
                marginBottom: 10,
              }}
            >
              Answer
            </div>
          )}

          {/* Message */}
          <div
            style={{
              textAlign: 'center',
              width: helpPanelWidth,
              maxWidth: '100%',
              alignSelf: 'center',
              marginBottom: 10,
            }}
          >
            <Readable
              highlightColor={READ_ALOUD_HIGHLIGHT_BLUE}
              text="When you close this message, we will replace your problem."
            />
          </div>

          {/* Scrollable Canvas */}
          <div
            style={{
              overflow: 'auto',
              padding: HELP_PANEL_PADDING,
              backgroundColor: 'white',
              flex: 1,
              minHeight: 0,
              width: helpPanelWidth,
              maxWidth: '100%', // prevent overflow beyond modal bounds on narrow/mobile screens
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'flex-start',
              alignSelf: 'center',
            }}
          >
            <div style={{ width: helpCanvasWidth, margin: '0 auto' }}>
              <ProblemCanvas
                problemCanvasOwner={problemCanvasOwner}
                problemData={assimilatedProblemData}
                problemWidth={helpCanvasWidth}
              />
            </div>
          </div>

          <div style={{ height: 10 }} />

          {/* OK Button */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: 15,
            }}
          >
            <ModalButton color={DIALOG_BLUE_BUTTON} text="OK" close={replace} />
          </div>
        </div>
      )}
    </DraggableResizable>
  );
};
